import React, { useEffect, useRef } from "react";
import { colors, primaryGradient } from "../../../theme/colors";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Box,
  Chip,
  Avatar,
  Paper,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CodeIcon from "@mui/icons-material/Code";
import FolderSpecialIcon from "@mui/icons-material/FolderSpecial";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import PsychologyIcon from "@mui/icons-material/Psychology";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { styled } from "@mui/material/styles";

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <Box display="flex" alignItems="center" gap={1.5} mb={2}>
    <Avatar
      sx={{
        bgcolor: colors.primaryLight,
        color: colors.primary,
        width: 40,
        height: 40,
      }}
    >
      <Icon fontSize="medium" />
    </Avatar>
    <Box>
      <Typography variant="h6" fontWeight="700" color={colors.black}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color={colors.text.muted}>
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${colors.border.light}`,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
  transition: "all 0.25s ease-in-out",
  backgroundColor: "#ffffff",
  "&:hover": {
    boxShadow: "0 8px 30px rgba(127, 29, 29, 0.08)",
    borderColor: colors.primaryLightBorder,
  },
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: "16px !important",
  border: `1px solid ${colors.border.light}`,
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.03)",
  marginBottom: theme.spacing(2),
  overflow: "hidden",
  backgroundColor: "#ffffff",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    borderColor: colors.primaryLightBorder,
    boxShadow: "0 6px 24px rgba(127, 29, 29, 0.08)",
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: "#fafafa",
  transition: "background-color 0.2s ease",
  padding: theme.spacing(1.5, 2.5),
  "&:hover": {
    backgroundColor: colors.primaryLight,
  },
  "&.Mui-expanded": {
    backgroundColor: colors.primaryLight,
    borderBottom: `1px solid ${colors.border.light}`,
  },
  "& .MuiAccordionSummary-content": {
    margin: 0,
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  backgroundColor: "#ffffff",
  padding: theme.spacing(3),
}));

const CandidateSummary = ({ data }) => {
  const summaryRef = useRef(null);

  useEffect(() => {
    if (data && summaryRef.current) {
      const timer = setTimeout(() => {
        summaryRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [data]);

  if (!data || !data.structured_content) return null;

  const {
    personal_details = {},
    summary = "",
    skills = {},
    work_experience = [],
    education = [],
    achievements = [],
    projects = [],
  } = data.structured_content;

  const getInitials = (name) => {
    if (!name) return "C";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <Box ref={summaryRef} sx={{ width: "100%", py: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              background: `linear-gradient(135deg, ${colors.primaryLight} 0%, #ffffff 100%)`,
              border: `1px solid ${colors.primaryLightBorder}`,
              boxShadow: "0 10px 30px rgba(127, 29, 29, 0.06)",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2.5}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    background: primaryGradient,
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    boxShadow: "0 4px 14px rgba(127, 29, 29, 0.3)",
                  }}
                >
                  {getInitials(personal_details.name)}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="700" color={colors.black}>
                    {personal_details.name || "Candidate Profile"}
                  </Typography>
                  <Typography variant="body2" color={colors.text.secondary}>
                    Structured Resume Insights
                  </Typography>
                </Box>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {personal_details.email && (
                  <Chip
                    icon={<EmailIcon style={{ color: colors.primary }} />}
                    label={personal_details.email}
                    variant="outlined"
                    sx={{
                      borderColor: colors.border.default,
                      bgcolor: "#ffffff",
                      fontWeight: 500,
                      "&:hover": { borderColor: colors.primary },
                    }}
                  />
                )}
                {personal_details.phone_number && (
                  <Chip
                    icon={<PhoneIcon style={{ color: colors.primary }} />}
                    label={personal_details.phone_number}
                    variant="outlined"
                    sx={{
                      borderColor: colors.border.default,
                      bgcolor: "#ffffff",
                      fontWeight: 500,
                      "&:hover": { borderColor: colors.primary },
                    }}
                  />
                )}
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {summary && (
          <Grid item xs={12}>
            <StyledCard variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <SectionHeader
                  icon={AssignmentIndIcon}
                  title="Executive Summary"
                  subtitle="Key overview of background and strengths"
                />
                <Divider sx={{ mb: 2, opacity: 0.6 }} />
                <Typography
                  variant="body1"
                  color={colors.text.primary}
                  sx={{ lineHeight: 1.7, fontSize: "0.975rem" }}
                >
                  {summary}
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
        )}

        {skills && Object.keys(skills).length > 0 && (
          <Grid item xs={12}>
            <StyledCard variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <SectionHeader
                  icon={CodeIcon}
                  title="Skills & Expertise"
                  subtitle="Core technical and interpersonal capabilities"
                />
                <Divider sx={{ mb: 2.5, opacity: 0.6 }} />
                <Grid container spacing={2.5}>
                  {Object.entries(skills).map(([category, skillList], index) => {
                    const isTechnical = category === "technical_skills";
                    const categoryLabel = isTechnical
                      ? "Technical Skills"
                      : category === "soft_skills"
                      ? "Soft Skills"
                      : category.replace("_", " ");

                    return (
                      <Grid item xs={12} md={6} key={index}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            bgcolor: isTechnical
                              ? "rgba(127, 29, 29, 0.02)"
                              : "rgba(99, 115, 129, 0.04)",
                            border: `1px solid ${colors.border.light}`,
                            height: "100%",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight="700"
                            color={colors.primary}
                            gutterBottom
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              textTransform: "capitalize",
                            }}
                          >
                            {isTechnical ? (
                              <CodeIcon fontSize="small" />
                            ) : (
                              <PsychologyIcon fontSize="small" />
                            )}
                            {categoryLabel}
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1} mt={1.5}>
                            {Array.isArray(skillList) ? (
                              skillList.map((skill, idx) => (
                                <Chip
                                  key={idx}
                                  label={skill}
                                  size="small"
                                  sx={{
                                    bgcolor: "#ffffff",
                                    border: `1px solid ${colors.border.default}`,
                                    color: colors.black,
                                    fontWeight: 500,
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                                    "&:hover": {
                                      bgcolor: colors.primaryLight,
                                      borderColor: colors.primaryLightBorder,
                                      color: colors.primary,
                                    },
                                  }}
                                />
                              ))
                            ) : (
                              <Typography variant="body2" color={colors.text.muted}>
                                {String(skillList)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </StyledCard>
          </Grid>
        )}

        {achievements && achievements.length > 0 && (
          <Grid item xs={12}>
            <StyledCard variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <SectionHeader
                  icon={EmojiEventsIcon}
                  title="Key Achievements"
                  subtitle="Recognitions, awards, and milestones"
                />
                <Divider sx={{ mb: 2, opacity: 0.6 }} />
                <Stack spacing={1.5}>
                  {achievements.map((achievement, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="flex-start"
                      gap={1.5}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: "#fafafa",
                        border: `1px solid ${colors.border.light}`,
                      }}
                    >
                      <EmojiEventsIcon
                        sx={{ color: "#d97706", fontSize: 20, mt: 0.2 }}
                      />
                      <Typography
                        variant="body2"
                        color={colors.text.primary}
                        fontWeight="500"
                      >
                        {achievement}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </StyledCard>
          </Grid>
        )}

        {work_experience && work_experience.length > 0 && (
          <Grid item xs={12}>
            <StyledAccordion defaultExpanded>
              <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar
                    sx={{
                      bgcolor: colors.primary,
                      color: "#ffffff",
                      width: 34,
                      height: 34,
                    }}
                  >
                    <WorkIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="h6" fontWeight="700" color={colors.black}>
                    Work Experience
                  </Typography>
                  <Chip
                    label={`${work_experience.length} ${
                      work_experience.length === 1 ? "Role" : "Roles"
                    }`}
                    size="small"
                    sx={{
                      bgcolor: colors.primaryLight,
                      color: colors.primary,
                      fontWeight: 600,
                      ml: 1,
                    }}
                  />
                </Box>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Stack spacing={2.5}>
                  {work_experience.map((job, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        borderColor: colors.border.light,
                        backgroundColor: "#ffffff",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: colors.primaryLightBorder,
                          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={1}
                        mb={1.5}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            fontWeight="700"
                            color={colors.black}
                          >
                            {job.job_title}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <BusinessIcon
                              fontSize="small"
                              sx={{ color: colors.text.muted, fontSize: 16 }}
                            />
                            <Typography
                              variant="body2"
                              fontWeight="600"
                              color={colors.primary}
                            >
                              {job.company}
                            </Typography>
                          </Box>
                        </Box>
                        {job.duration && (
                          <Chip
                            icon={
                              <CalendarTodayIcon
                                style={{ fontSize: 14, color: colors.text.muted }}
                              />
                            }
                            label={job.duration}
                            size="small"
                            variant="outlined"
                            sx={{
                              bgcolor: "#f9fafb",
                              borderColor: colors.border.default,
                              color: colors.text.secondary,
                              fontWeight: 500,
                            }}
                          />
                        )}
                      </Stack>

                      {job.responsibilities && job.responsibilities.length > 0 && (
                        <Box mt={2}>
                          <Typography
                            variant="caption"
                            fontWeight="700"
                            color={colors.text.muted}
                            sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                          >
                            Key Responsibilities
                          </Typography>
                          <Stack spacing={1} mt={1}>
                            {job.responsibilities.map((resp, idx) => (
                              <Box
                                key={idx}
                                display="flex"
                                alignItems="flex-start"
                                gap={1.2}
                              >
                                <CheckCircleOutlineIcon
                                  sx={{
                                    color: colors.primary,
                                    fontSize: 16,
                                    mt: 0.3,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  color={colors.text.primary}
                                  sx={{ lineHeight: 1.6 }}
                                >
                                  {resp}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </StyledAccordionDetails>
            </StyledAccordion>
          </Grid>
        )}

        {education && education.length > 0 && (
          <Grid item xs={12}>
            <StyledAccordion defaultExpanded>
              <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar
                    sx={{
                      bgcolor: colors.primary,
                      color: "#ffffff",
                      width: 34,
                      height: 34,
                    }}
                  >
                    <SchoolIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="h6" fontWeight="700" color={colors.black}>
                    Education
                  </Typography>
                </Box>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Grid container spacing={2}>
                  {education.map((edu, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          borderRadius: 3,
                          borderColor: colors.border.light,
                          height: "100%",
                          backgroundColor: "#ffffff",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            fontWeight="700"
                            color={colors.black}
                          >
                            {edu.degree}
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            color={colors.primary}
                            sx={{ mt: 0.5 }}
                          >
                            {edu.university}
                          </Typography>
                        </Box>
                        {edu.duration && (
                          <Box display="flex" alignItems="center" gap={0.8} mt={2}>
                            <CalendarTodayIcon
                              sx={{ color: colors.text.muted, fontSize: 14 }}
                            />
                            <Typography
                              variant="caption"
                              color={colors.text.secondary}
                              fontWeight="500"
                            >
                              {edu.duration}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </StyledAccordionDetails>
            </StyledAccordion>
          </Grid>
        )}

        {projects && projects.length > 0 && (
          <Grid item xs={12}>
            <StyledAccordion>
              <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar
                    sx={{
                      bgcolor: colors.primary,
                      color: "#ffffff",
                      width: 34,
                      height: 34,
                    }}
                  >
                    <FolderSpecialIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="h6" fontWeight="700" color={colors.black}>
                    Projects
                  </Typography>
                  <Chip
                    label={projects.length}
                    size="small"
                    sx={{
                      bgcolor: colors.primaryLight,
                      color: colors.primary,
                      fontWeight: 600,
                      ml: 1,
                    }}
                  />
                </Box>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Stack spacing={2.5}>
                  {projects.map((project, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        borderColor: colors.border.light,
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight="700"
                          color={colors.black}
                        >
                          {project.project_title}
                        </Typography>
                        {project.duration && (
                          <Chip
                            label={project.duration}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: colors.border.default }}
                          />
                        )}
                      </Stack>
                      {project.description && (
                        <Typography
                          variant="body2"
                          color={colors.text.secondary}
                          mb={1.5}
                          sx={{ lineHeight: 1.6 }}
                        >
                          {project.description}
                        </Typography>
                      )}
                      {project.responsibilities &&
                        project.responsibilities.length > 0 && (
                          <Stack spacing={0.8} mt={1}>
                            {project.responsibilities.map((resp, idx) => (
                              <Box
                                key={idx}
                                display="flex"
                                alignItems="flex-start"
                                gap={1}
                              >
                                <CheckCircleOutlineIcon
                                  sx={{
                                    color: colors.primary,
                                    fontSize: 15,
                                    mt: 0.3,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  color={colors.text.primary}
                                >
                                  {resp}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        )}
                    </Paper>
                  ))}
                </Stack>
              </StyledAccordionDetails>
            </StyledAccordion>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CandidateSummary;
